import map from 'lodash/map'
import get from 'lodash/get'
import type { FunctionComponent } from 'react'
import React, { useMemo } from 'react'
import type { DragSortHandleProps, DragSortItemProps } from '@ztechapps/webui.util'
import type { RowProps } from './Row'
import { HandlePropsProvider } from './HandlePropsContext'
import { selectedRowClassName } from '../DataTable/selectedRow'

type ActualRowProps = {
  dataArray: any[]
  createRow?: any
  row?: FunctionComponent<RowProps>
  selectable?: boolean
  onSelect?: any
  idFieldName?: string
  handleSelect?: any
  currentItem?: any
  getItemProps?: (index: number) => DragSortItemProps
  getHandleProps?: (index: number) => DragSortHandleProps
  dragRowClassName?: string
}

type MemoizedRowProps = {
  Row: any
  rowData: any
  config: any
  handleProps: DragSortHandleProps | null
}

// Memoized individual row component. The comparator compares the SEMANTIC
// values that affect rendering (data-dragging / data-drop-target / data-drop-side
// on dragProps, and draggable on handleProps) rather than the container object
// identity. Without this, every dragover event (60fps during drag) would
// produce new prop-getter object references and force every row to re-render
// — causing noticeable stutter on tables with deep row trees.
const MemoizedRow = React.memo(
  ({ Row, rowData, config, handleProps }: MemoizedRowProps) => {
    // React reserves the `key` prop name. Spreading `rowData` directly emits two DEV warnings
    // when a row entity has a `key` field (e.g. `ProductMetadata.key` carries the spec name).
    // React strips it from the props anyway, so consumers read the row's key field from canonical
    // state — never from `props.key`. Stripping at the spread site eliminates the warnings and
    // matches the runtime contract.
    const { key: _ignoredKey, ...rowDataWithoutKey } = (rowData ?? {}) as Record<string, unknown>
    return (
      <HandlePropsProvider value={handleProps}>
        <Row {...{ _config: config, ...rowDataWithoutKey }} />
      </HandlePropsProvider>
    )
  },
  (prevProps, nextProps) => {
    const prevDrag = prevProps.config.dragProps
    const nextDrag = nextProps.config.dragProps
    return (
      prevProps.rowData === nextProps.rowData &&
      prevProps.config.key === nextProps.config.key &&
      prevProps.config.highlightClassName === nextProps.config.highlightClassName &&
      prevProps.config.onSelect === nextProps.config.onSelect &&
      prevProps.config.dragRowClassName === nextProps.config.dragRowClassName &&
      prevDrag?.['data-dragging'] === nextDrag?.['data-dragging'] &&
      prevDrag?.['data-drop-target'] === nextDrag?.['data-drop-target'] &&
      prevDrag?.['data-drop-side'] === nextDrag?.['data-drop-side'] &&
      prevProps.handleProps?.draggable === nextProps.handleProps?.draggable
    )
  }
)

export const ActualRow = React.memo(
  ({
    dataArray,
    createRow,
    row,
    selectable,
    onSelect,
    idFieldName = '_id',
    handleSelect,
    currentItem,
    getItemProps,
    getHandleProps,
    dragRowClassName,
  }: ActualRowProps) => {
    const mapResult = useMemo(() => {
      return map(dataArray, (m, i) => {
        let highlightClassName = ''

        //if it is selectable then show the highlighting row class
        if (selectable || onSelect) {
          if (currentItem && currentItem[idFieldName] === ((m && m[idFieldName]) || currentItem?._selected)) {
            highlightClassName = selectedRowClassName
          }
        }

        //the config that can be passed on the row
        const key = get(m, idFieldName, i)
        const dragProps = getItemProps?.(i)
        // `handleProps` is transport-only — passed to MemoizedRow which hands
        // it to `<HandlePropsProvider>`. Deliberately NOT stored in `config`
        // because row components consume it via `useHandleProps()` from the
        // context, not via `_config.handleProps`.
        const handleProps = getHandleProps?.(i) ?? null
        const config = {
          onSelect: handleSelect,
          highlightClassName,
          index: i,
          key,
          dragProps,
          dragRowClassName,
        }

        if (createRow) {
          //deprecate this usage, it can one using the row field
          return createRow(m, i, config)
        } else {
          //it uses hooks so we need ro run it like jsx
          return <MemoizedRow key={key} Row={row} rowData={m} config={config} handleProps={handleProps} />
        }
      })
    }, [
      dataArray,
      createRow,
      row,
      selectable,
      onSelect,
      idFieldName,
      handleSelect,
      currentItem,
      getItemProps,
      getHandleProps,
      dragRowClassName,
    ])

    return mapResult
  }
)
